import React, { type ReactNode } from "react";
import { Text, Box, Flex, AspectRatio, type FlexProps, type IconProps, type AspectRatioProps } from "@chakra-ui/react";
import { ArrowLink } from "ui/link";
import Image, { type StaticImageData } from "next/image";
import { useHeaderHeight } from "contexts/header-height";

type ProductPairProps = {
  align: "left" | "right";
  animationElement: ReactNode;
  animationProps?: AspectRatioProps;
  mobileImage: StaticImageData;
  eyebrow?: string;
  eyebrowIcon?: (props: IconProps) => JSX.Element;
  title: string;
  description: string;
  actionUrl?: string;
  actionText?: string;
  theme?: "green" | "blue";
};

export function ProductPair({
  align,
  animationElement,
  animationProps,
  mobileImage,
  eyebrow,
  eyebrowIcon,
  title,
  description,
  actionUrl,
  actionText = "Learn more",
  theme = "green",
  ...props
}: ProductPairProps & FlexProps) {
  const { headerHeight } = useHeaderHeight();

  return (
    <Flex
      align={{ base: "start", md: "center" }}
      direction={{ base: "column", md: "row" }}
      gap={{ base: 9, md: 12, lg: 16, xl: 40 }}
      w="100%"
      maxW={{ base: "mediaMaxW.mobile", md: "100%" }}
      mx="auto"
      scrollMarginTop={`${headerHeight}px`}
      {...props}
    >
      <Box flex={1} order={{ base: 1, md: align === "left" ? 1 : 2 }} width="100%">
        {eyebrow && (
          <Flex align="center" gap={3} fontSize={{ base: "sm", md: "md" }} textStyle="uppercase" mb={5}>
            {eyebrowIcon && <Box as={eyebrowIcon} boxSize={{ base: "24px", md: "30px" }} color="grass" />}

            <Text as="span" fontSize={{ base: "xs", sm: "sm", lg: "md" }}>
              {eyebrow}
            </Text>
          </Flex>
        )}

        <Text as="h3" textStyle="h6" whiteSpace="pre-wrap">
          {title}
        </Text>

        <Text fontSize={{ base: "md", md: "lg" }} mt={5} w="100%">
          {description}
        </Text>

        {actionUrl && (
          <ArrowLink href={actionUrl} variant="grass" size={{ base: "md", md: "lg" }} mt={6}>
            {actionText}
          </ArrowLink>
        )}
      </Box>

      <Box
        bg={`${theme}.50`}
        borderRadius="12px"
        border="1px"
        borderColor={theme === "green" ? "mint" : "cyan"}
        cursor="default"
        flex={1}
        order={{ base: 2, md: align === "left" ? 2 : 1 }}
        w={{ base: "100%", sm: "440px", md: "540px" }}
        maxW="100%"
        mx="auto"
        overflow="hidden"
      >
        <AspectRatio display={{ base: "none", md: "block" }} ratio={5 / 4} pos="relative" {...animationProps}>
          <Box boxSize="100%" fontFamily="inter">
            {animationElement}
          </Box>
        </AspectRatio>

        <AspectRatio display={{ base: "block", md: "none" }} ratio={5 / 4} pos="relative" w="100%">
          <Image fill src={mobileImage} alt={title} sizes="100%" style={{ objectFit: "cover" }} />
        </AspectRatio>
      </Box>
    </Flex>
  );
}
